import { useRef, useState } from "react";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, imgUrl } from "@/lib/api";
import Lightbox from "@/components/Lightbox";

const norm = (arr) =>
  (arr || []).map((it) => (typeof it === "string" ? { url: it, label: "" } : { url: it.url, label: it.label || "" }));

/**
 * Edit a list of photos for one realization (before/after).
 * Each item is {url, label} where label is "" | "pred" | "po".
 */
export function PhotoGalleryEditor({ images = [], onChange, testid = "gallery", label = "Pridať fotky" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(-1);
  const items = norm(images);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const uploaded = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) { toast.error(`${file.name}: nie je obrázok`); continue; }
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: max 8 MB`); continue; }
      try { uploaded.push({ url: await uploadImage(file), label: "" }); }
      catch (err) { toast.error(err?.response?.data?.detail || "Nahrávanie zlyhalo"); }
    }
    if (uploaded.length) { onChange([...items, ...uploaded]); toast.success(`Nahraných fotiek: ${uploaded.length}`); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setLabel = (i, lbl) => onChange(items.map((it, idx) => idx === i ? { ...it, label: it.label === lbl ? "" : lbl } : it));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3 mb-2">
        {items.map((it, i) => (
          <div key={i} className="w-20">
            <div className="relative w-20 h-20 border border-white/15 overflow-hidden group">
              <button type="button" onClick={() => setZoom(i)} className="w-full h-full">
                <img src={imgUrl(it.url)} alt="" className="w-full h-full object-cover" />
              </button>
              {it.label && <span className="absolute bottom-0 left-0 bg-gold text-ink text-[9px] font-bold px-1 uppercase">{it.label}</span>}
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
                className="absolute top-0 right-0 bg-black/70 text-red-300 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex gap-1 mt-1">
              <button type="button" onClick={() => setLabel(i, "pred")} data-testid={`${testid}-pred-${i}`}
                className={`flex-1 text-[10px] py-0.5 border ${it.label === "pred" ? "bg-gold text-ink border-gold" : "border-white/20 text-white/50 hover:border-gold"}`}>Pred</button>
              <button type="button" onClick={() => setLabel(i, "po")} data-testid={`${testid}-po-${i}`}
                className={`flex-1 text-[10px] py-0.5 border ${it.label === "po" ? "bg-gold text-ink border-gold" : "border-white/20 text-white/50 hover:border-gold"}`}>Po</button>
            </div>
          </div>
        ))}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
        className="flex items-center gap-2 border border-white/20 hover:border-gold text-white/80 hover:text-gold px-4 py-2 text-sm transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {label}
      </button>
      {zoom >= 0 && <Lightbox images={items} index={zoom} onClose={() => setZoom(-1)} />}
    </div>
  );
}

export default PhotoGalleryEditor;
